import React from "react";
import { useSearchParams } from "react-router-dom";

import BaseAutocomplete from "./Base/BaseAutocomplete";

import { useLazyGetDDLSystemUserListQuery } from "store/services/userApi";

import { useAuth, useGetUrlParams } from "hooks";

import { camelCasefy } from "utils/stringHelpers";

const ParamsUserAutocomplete = ({
  label = "Responsible",
  onChange,
  ...props
}) => {
  const { viewEntity, isAccountAdmin, isSystemAdmin } = useAuth();

  const urlParams = useGetUrlParams();

  const [searchParams, setSearchParams] = useSearchParams();

  // RTK Query
  const [
    fetchUsers,
    { data: userList, isLoading: loadingUsers, isFetching: fetchingUsers },
  ] = useLazyGetDDLSystemUserListQuery();

  const mappedUserList = React.useMemo(() => {
    const __getUserLabel = (user) => {
      const { name, entity, designation } = user;

      let label = name;

      if (designation) label = label + " | " + designation;

      if ((isAccountAdmin || isSystemAdmin) && entity?.displayName)
        label = label + " | " + entity?.displayName;

      return label;
    };

    const _mappedList = userList?.map((el) => ({
      label: __getUserLabel(el),
      id: el?._id,
    }));

    return !!_mappedList?.length
      ? [{ label: "Unassigned", id: "N/A" }, ..._mappedList]
      : [];
  }, [isAccountAdmin, isSystemAdmin, userList]);

  const paramUser = React.useMemo(() => {
    const userRef = searchParams.get(camelCasefy(label));

    if (!userRef) return null;

    return (
      mappedUserList?.find(
        (el) => el?.id === userRef || el.label === userRef
      ) ?? null
    );
  }, [label, searchParams, mappedUserList]);

  React.useEffect(() => {
    fetchUsers({
      ...(!isSystemAdmin &&
        !isAccountAdmin && { entity: viewEntity?.entity?._id }),
    });
  }, [fetchUsers, isAccountAdmin, isSystemAdmin, viewEntity?.entity?._id]);

  return (
    <BaseAutocomplete
      label={label}
      value={paramUser}
      options={mappedUserList}
      onChange={(_, user) => {
        if (user)
          return setSearchParams({
            ...urlParams,
            [camelCasefy(label)]: user.id,
          });

        delete urlParams[camelCasefy(label)];
        setSearchParams(urlParams);
      }}
      loading={loadingUsers || fetchingUsers}
      loadingText={`Loading ${label}(s)...`}
      {...props}
    />
  );
};

export default ParamsUserAutocomplete;
